import { Box, TextField } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { Controller, useFormContext } from 'react-hook-form';
import { RHFCheckbox, RHFTextField } from 'src/components/hook-form';
import { translateKey } from 'src/locales/translate';
import { SPAN_FULL, SPAN_WIDE } from './medicationFormLayout';

interface MedicationClinicalSectionProps {
  disabled?: boolean;
  /**
   * A medication being added has no course behind it yet, so there is nothing
   * to report a reaction to. The field appears once the record exists.
   */
  showAdverseReaction?: boolean;
}

/**
 * Why the patient is on it, from when, and whether it disagreed with them. The
 * indication is what tells a vet six months later whether the steroid was for
 * atopy or for an autoimmune disease.
 */
export default function MedicationClinicalSection({
  disabled,
  showAdverseReaction = true,
}: MedicationClinicalSectionProps) {
  const { control, watch } = useFormContext();
  const adverseReaction = Boolean(watch('adverseReaction'));

  return (
    <>
      <Box sx={SPAN_WIDE}>
        <Controller
          name="startedAt"
          control={control}
          render={({ field, fieldState }) => (
            <DesktopDatePicker
              label={translateKey('medication.form.startedAt')}
              value={field.value}
              onChange={field.onChange}
              disabled={disabled}
              inputFormat="dd.MM.yyyy"
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ??
                    translateKey('medication.form.startedAtHint')
                  }
                />
              )}
            />
          )}
        />
      </Box>

      {/* Roomy on purpose: the indication is where a vet writes why, in a
          sentence, not a word. */}
      <Box sx={SPAN_WIDE}>
        <RHFTextField
          name="indication"
          fullWidth
          multiline
          rows={3}
          label={translateKey('medication.form.indication')}
          disabled={disabled}
          helperText={translateKey('medication.form.indicationHint')}
        />
      </Box>

      {showAdverseReaction && (
        <Box sx={SPAN_FULL}>
          <RHFCheckbox
            name="adverseReaction"
            label={translateKey('medication.form.adverseReaction')}
            disabled={disabled}
          />
        </Box>
      )}

      {showAdverseReaction && adverseReaction && (
        <Box sx={SPAN_WIDE}>
          <RHFTextField
            name="adverseReactionNote"
            fullWidth
            label={translateKey('medication.form.adverseReactionNote')}
            multiline
            // Same height as the indication: both are places to describe.
            rows={3}
            required
            disabled={disabled}
          />
        </Box>
      )}
    </>
  );
}
